import { useNavigate } from 'react-router-dom';

const WhereToBegin = () => {
  const navigate = useNavigate();

  const handleCTA = () => {
    navigate('/clinical-services');
  };

  return (
    <section className="py-10 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white relative overflow-hidden">
      {/* Top separator */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-bronze/20 to-transparent"></div>

      {/* Background texture */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sand/20 rounded-full blur-[120px] opacity-60 translate-x-1/3 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-bronze/5 rounded-full blur-[100px] opacity-50"></div>
      </div>

      <div className="relative max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8 sm:mb-20">
          <div className="flex items-center gap-3 mb-3 sm:mb-6">
            <div className="w-8 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
              Where to Begin
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 sm:gap-6 items-end">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-light text-charcoal italic leading-tight">
              Your <em className="not-italic text-bronze">Stability Pathway</em>
            </h2>
            <p className="hidden sm:block text-textGray text-[13px] font-sans font-light leading-relaxed lg:text-right lg:max-w-sm">
              Every lasting transformation begins with a single, well-structured first step.
            </p>
          </div>
        </div>

        {/* Pathway Steps */}
        <div className="relative flex flex-col gap-0">

          {/* Vertical connector line */}
          <div className="hidden sm:block absolute left-[39px] top-[56px] bottom-[56px] w-[1px] bg-gradient-to-b from-bronze/40 via-bronze/20 to-transparent pointer-events-none"></div>

          {/* Step 1 */}
          <div className="relative flex flex-col sm:flex-row gap-4 sm:gap-10 mb-6 sm:mb-12">
            {/* Step number */}
            <div className="flex-shrink-0 flex flex-row sm:flex-col items-center gap-3 sm:gap-0">
              <div className="w-[56px] h-[56px] sm:w-[80px] sm:h-[80px] rounded-full bg-charcoal flex items-center justify-center shadow-elevated z-10 relative">
                <span className="font-display text-white text-base sm:text-xl italic font-light">01</span>
              </div>
            </div>
            {/* Content card */}
            <div className="flex-1 bg-warmWhite border border-warmGray/20 rounded-2xl px-5 py-5 sm:px-10 sm:py-10 shadow-soft">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
                <span className="inline-flex items-center gap-2 border border-bronze/30 rounded-full px-3 py-1 sm:px-4 sm:py-1.5">
                  <i className="ri-time-line text-bronze text-[11px]"></i>
                  <span className="text-bronze text-[9px] font-sans tracking-[0.25em] uppercase font-semibold whitespace-nowrap">90 min</span>
                </span>
                <span className="inline-flex items-center gap-2 bg-bronze/8 rounded-full px-3 py-1 sm:px-4 sm:py-1.5">
                  <i className="ri-star-line text-bronze/70 text-[11px]"></i>
                  <span className="text-bronze/80 text-[9px] font-sans tracking-[0.25em] uppercase font-semibold whitespace-nowrap">Required for all new clients</span>
                </span>
              </div>
              <h3 className="font-display text-charcoal text-xl sm:text-2xl italic font-light leading-snug mb-3 sm:mb-5">
                Stability Signature Session
              </h3>
              <p className="text-charcoal/60 text-[12px] sm:text-[13px] font-sans font-light leading-relaxed mb-4 sm:mb-8 max-w-xl">
                All new clients begin with the 90-minute Stability Signature — a comprehensive, full-intake session designed to understand your unique pattern of tension, regulation, and structural holding.
              </p>
              {/* What's included */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                {[
                  { icon: 'ri-search-eye-line', label: 'Vionne Stability Method — Assessment', desc: 'Mapping tension patterns, jaw holding, and nervous system load.' },
                  { icon: 'ri-sparkling-2-line', label: 'Identify Vionne Stability Facial Archetypes', desc: 'Pinpointing your dominant facial archetype to guide precision treatment sequencing.' },
                  { icon: 'ri-file-list-3-line', label: 'Tailored Treatment Plan', desc: 'A personalised protocol built around your specific profile.' },
                  { icon: 'ri-leaf-line', label: 'Integrated First Treatment', desc: 'Acupuncture, botanical support, and regulation work — all within one session.' },
                  { icon: 'ri-compass-3-line', label: 'Pathway Recommendation', desc: 'Clear guidance on what comes next, based on your assessment results.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-white border border-warmGray/15">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-bronze text-[12px]`}></i>
                    </div>
                    <div>
                      <p className="font-sans text-charcoal text-[10.5px] font-semibold mb-0.5 leading-snug">{item.label}</p>
                      <p className="font-sans text-charcoal/50 text-[10px] font-light leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative flex flex-col sm:flex-row gap-4 sm:gap-10 mb-6 sm:mb-12">
            {/* Step number */}
            <div className="flex-shrink-0 flex flex-row sm:flex-col items-center gap-3 sm:gap-0">
              <div className="w-[56px] h-[56px] sm:w-[80px] sm:h-[80px] rounded-full bg-white border border-warmGray/25 flex items-center justify-center shadow-soft z-10 relative">
                <span className="font-display text-charcoal/50 text-base sm:text-xl italic font-light">02</span>
              </div>
            </div>
            {/* Content card */}
            <div className="flex-1 bg-warmWhite border border-warmGray/20 rounded-2xl px-5 py-5 sm:px-10 sm:py-10 shadow-soft">
              <h3 className="font-display text-charcoal text-xl sm:text-2xl italic font-light leading-snug mb-3 sm:mb-5">
                Your Personalised Follow-On Plan
              </h3>
              <p className="text-charcoal/60 text-[12px] sm:text-[13px] font-sans font-light leading-relaxed mb-4 sm:mb-8 max-w-xl">
                Based on your Stability Signature assessment, a follow-on schedule is designed specifically for you. No two pathways are the same — your next appointments are determined by what your body and nervous system actually need.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                {[
                  { icon: 'ri-calendar-check-line', label: 'Scheduled at your own pace', desc: 'Sessions are spaced according to your regulation rhythm and lifestyle.' },
                  { icon: 'ri-route-line', label: 'Evolving protocol', desc: 'Your treatment plan is reviewed and refined as your stability deepens.' },
                  { icon: 'ri-focus-2-line', label: 'Targeted focus areas', desc: 'Each session builds on the last — addressing the layers identified in your assessment.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-bronze/70 text-sm sm:text-base`}></i>
                    </div>
                    <div>
                      <span className="font-sans text-charcoal text-[12px] font-semibold">{item.label}</span>
                      <span className="font-sans text-charcoal/50 text-[11px] font-light ml-2">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8 sm:my-16">
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-bronze/20"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-bronze/40"></div>
          <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-bronze/20"></div>
        </div>

        {/* CTA Block */}
        <div className="text-center flex flex-col items-center">
          <p className="text-textGray text-[12px] sm:text-[13px] font-sans font-light leading-relaxed max-w-md mx-auto mb-6 sm:mb-10 italic">
            Lasting change does not begin with a product or a routine.<br />
            It begins with understanding what your face and nervous system are actually holding.
          </p>
          <button
            onClick={handleCTA}
            className="group inline-flex items-center gap-3 sm:gap-4 cursor-pointer"
          >
            <span className="px-8 sm:px-12 py-3 sm:py-4 bg-charcoal text-white rounded-full text-[10px] sm:text-[11px] font-sans font-semibold tracking-[0.22em] uppercase group-hover:bg-bronze transition-colors duration-400 whitespace-nowrap shadow-elevated">
              Start your true lasting change
            </span>
            <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-full border border-charcoal/20 flex items-center justify-center group-hover:border-bronze group-hover:bg-bronze transition-all duration-400">
              <i className="ri-arrow-right-line text-charcoal text-sm group-hover:text-white group-hover:translate-x-0.5 transition-transform duration-300"></i>
            </span>
          </button>
          <p className="text-[10px] font-sans text-charcoal/35 tracking-wide mt-4 sm:mt-6 italic">
            All new clients begin with the 90-minute Stability Signature session.
          </p>
        </div>

      </div>

      {/* Bottom separator */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-bronze/20 to-transparent"></div>
    </section>
  );
};

export default WhereToBegin;